import { getCookie } from '@scripts/aem.js';
import { parseDate } from '@helpers/formatters/date.js';
import { pushToDataLayer } from './data-layer.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PageViewWebPageDetails {
  server?: string;
  previousURL?: string;
  isErrorPage?: boolean;
  pageError?: string;
  country?: string;
  author?: string;
  publishDate?: string;
  trackingCode?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmID?: string;
  utmTerm?: string;
  utmContent?: string;
  industry?: string;
  function?: string;
  initiatives?: string;
  partnerType?: string;
  topic?: string;
  modules?: Array<{ moduleName: string }>;
  pageViews?: { value: number };
  path?: string;
  fullPath?: string;
  URLQuery?: string;
  directory?: string;
  subdomain?: string;
  title?: string;
  language?: string;
  [key: string]: unknown;
}

interface PageViewCelonisUser {
  userID: undefined;
  externalID: {
    adobeID: string;
    googleID: string;
  };
}

interface PageViewPayload {
  event: string;
  web: { webPageDetails: PageViewWebPageDetails };
  _celonis: {
    user: PageViewCelonisUser;
    environment: { browserDetails: { userAgent: string } };
    device: { type: undefined };
  };
  [key: string]: unknown;
}

declare global {
  interface Window {
    isErrorPage?: boolean;
    errorCode?: string;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getSubdomain(hostname: string): string | undefined {
  const parts = hostname.split('.');
  if (parts.length > 2) {
    const subdomain = parts.slice(0, -2).join('.');
    const subdomainMap: Record<string, string> = {
      www: 'commercial',
    };
    return subdomainMap[subdomain] || subdomain;
  }
  return undefined;
}

function getTrackingCode(urlParams: URLSearchParams): string | undefined {
  const campID = urlParams.get('campID');

  if (campID) {
    return campID;
  }

  const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_id', 'utm_term', 'utm_content'];
  const utmValues = utmParams.map((param) => urlParams.get(param) || 'undefined');

  if (utmValues.some((value) => value !== 'undefined')) {
    return `UTM|${utmValues.join('|')}`;
  }

  return undefined;
}

function getPreviousURL(): string | undefined {
  const docReferrer = document.referrer;

  if (docReferrer) {
    const referrerHost = new URL(docReferrer).hostname;
    if (referrerHost === window.location.hostname) {
      return docReferrer;
    }
  }
  return undefined;
}

function getPublishDate(): string | undefined {
  const dateFromMeta = document.head.querySelector<HTMLMetaElement>('meta[name="publication-date"]');
  if (dateFromMeta) {
    const date = parseDate(dateFromMeta.content);
    return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
  }
  return undefined;
}

function getModules(): Array<{ moduleName: string }> | undefined {
  const main = document.querySelector('main');
  const modules: Array<{ moduleName: string }> = [];
  if (!main) return undefined;
  main.querySelectorAll('div.section > div > div').forEach((block) => {
    modules.push({ moduleName: block.classList[0] });
  });
  return modules.length ? modules : undefined;
}

/**
 * Builds the `webPageDetails`/`_celonis` context object describing the current
 * page view (UTM tracking code, publish date, page modules, previous URL, etc.).
 */
function buildPageViewPayload(): PageViewPayload {
  const urlParams = new URLSearchParams(window.location.search);
  const directoryLevel = document.documentElement.dataset.countryCode === 'en' ? 1 : 2;

  const webPageDetails: PageViewWebPageDetails = {
    server: window.location.hostname,
    previousURL: getPreviousURL(),
    isErrorPage: window.isErrorPage || false,
    pageError: window.errorCode || 'no error found',
    country: sessionStorage.getItem('selectedCountry') || undefined,
    author: document.head.querySelector<HTMLMetaElement>('meta[name="author"]')?.content || undefined,
    publishDate: getPublishDate(),
    trackingCode: getTrackingCode(urlParams),
    utmSource: urlParams.get('utm_source') || undefined,
    utmMedium: urlParams.get('utm_medium') || undefined,
    utmCampaign: urlParams.get('utm_campaign') || undefined,
    utmID: urlParams.get('utm_id') || undefined,
    utmTerm: urlParams.get('utm_term') || undefined,
    utmContent: urlParams.get('utm_content') || undefined,
    industry: document.head.querySelector<HTMLMetaElement>('meta[name="industry"]')?.content?.replace('key:', '') || undefined,
    function: document.head.querySelector<HTMLMetaElement>('meta[name="function"]')?.content?.replace('key:', '') || undefined,
    initiatives: document.head.querySelector<HTMLMetaElement>('meta[name="initiatives"]')?.content?.replace('key:', '') || undefined,
    partnerType: document.head.querySelector<HTMLMetaElement>('meta[name="partner-type"]')?.content?.replace('key:', '') || undefined,
    topic: document.head.querySelector<HTMLMetaElement>('meta[name="topic"]')?.content?.replace('key:', '') || undefined,
    modules: getModules(),
    pageViews: { value: 1 },
    path: window.location.pathname,
    fullPath: window.location.origin + window.location.pathname,
    URLQuery: window.location.search || undefined,
    directory: window.location.pathname.split('/')[directoryLevel] || undefined,
    subdomain: getSubdomain(window.location.hostname),
    title: document.title.toLowerCase(),
    language: document.documentElement.dataset.countryCode || '',
  };

  return {
    event: 'pageView',
    web: { webPageDetails },
    _celonis: {
      user: {
        userID: undefined,
        externalID: {
          adobeID: getCookie('AMCV') || 'no adobe id cookie found',
          googleID: `${getCookie('_ga') || 'no _ga cookie found'} | ${getCookie('NID') || 'no NID cookie found'}`,
        },
      },
      environment: {
        browserDetails: { userAgent: navigator.userAgent },
      },
      device: { type: undefined },
    },
  };
}

/**
 * Pushes a `pageView` event (with UTM tracking code, publish date, page
 * modules, subdomain, previous URL, etc.) to `window.adobeDataLayer`.
 * Adobe Launch's data elements/rules read this context to build their own
 * XDM payload — this replaces the alloy `onBeforeEventSend` XDM-building
 * pipeline that was removed along with `src/scripts/martech/`.
 */
export function pushPageView(): void {
  pushToDataLayer(buildPageViewPayload());
}
